import React, { useContext, useState, useEffect, createContext } from "react";
import { mutate } from "swr";

import firebase from "firebaseFactory";
import { getCacheKeyForFunction } from "hooks";

// this is a workaround as we can't alias imports under CRA
// so we just directly import our permissions code from here
// eslint-disable-next-line no-restricted-imports
import {
  buildPermissionEnforcerFor,
  UserRole,
  Permissions,
  PermissionEnforcer,
} from "../../../../../common/authorization/permissions";

export { UserRole, Permissions };

interface SimpleFirebaseUser {
  uid: string;
  displayName: string | null;
  photoURL: string | null;
  email: string | null;
}

export interface AuthState {
  authenticated: boolean;
  initializing: boolean;
  user?: SimpleFirebaseUser;
  siteIds: string[];
  enforcer: PermissionEnforcer;
}

export const UnauthenticatedAuthState = {
  authenticated: false,
  initializing: false,
  siteIds: [],
  enforcer: buildPermissionEnforcerFor({
    role: UserRole.None,
    userId: undefined,
    permittedSiteIds: [],
  }),
};

export const createAuthStateFor = (
  siteIds: string[],
  role: UserRole,
  user: SimpleFirebaseUser
) => {
  return {
    authenticated: true,
    initializing: false,
    user: user,
    siteIds: siteIds,
    enforcer: buildPermissionEnforcerFor({
      userId: user?.uid,
      permittedSiteIds: siteIds,
      role: role,
    }),
  };
};

const defaultAuthState = {
  ...UnauthenticatedAuthState,
  initializing: true,
};

export const AuthDataContext = createContext<AuthState>(defaultAuthState);

export const AuthDataProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);

  useEffect(() => {
    let unsubscribe: VoidFunction | null = null;

    return firebase.auth().onAuthStateChanged(async (user) => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      if (user) {
        const updateStateFromToken = (
          tokenResult: firebase.auth.IdTokenResult
        ) => {
          const siteIds = Object.keys(tokenResult.claims.siteIds || {});
          const role = tokenResult.claims.role || false;
          setAuthState(createAuthStateFor(siteIds, role, user));
          mutate(
            getCacheKeyForFunction(firebase.function("getAllPermittedDevices"))
          );
        };
        // in a normal flow, we just read the token without refresh
        updateStateFromToken(await user.getIdTokenResult(false));
        // because we add claims to a user when the account is first created,
        // ie if they are a relectrify user or not
        // and won't automatically refresh claims, watch for changes on this key
        // if it changes to non-empty, then we refresh the claims, and set
        // the watched key back to empty again
        const metadataRef = firebase
          .database(firebase.shards.Customer)
          .ref(`users/${user.uid}/refreshClaims`);
        const listener = metadataRef.on("value", async (doc) => {
          if (doc.val()) {
            console.log("Detected request to refresh user claims");
            metadataRef.set({});
            updateStateFromToken(await user.getIdTokenResult(true));
          }
        });
        unsubscribe = () => metadataRef.off("value", listener);
      } else {
        setAuthState(UnauthenticatedAuthState);
        mutate(
          getCacheKeyForFunction(firebase.function("getAllPermittedDevices"))
        );
      }
    });
  }, [setAuthState]);

  return <AuthDataContext.Provider value={authState} children={children} />;
};

export const useAuthDataContext = () => useContext(AuthDataContext);
